import React, { useCallback, useEffect, useState } from 'react';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const STATUS_TABS = [
  { id: 'new',        label: 'New' },
  { id: 'in_review',  label: 'In review' },
  { id: 'quoted',     label: 'Quoted' },
  { id: 'completed',  label: 'Completed' },
  { id: 'declined',   label: 'Declined' },
  { id: 'all',        label: 'All' },
];

const STATUS_PILL = {
  new:        'bg-amber-50 text-amber-700 border border-amber-200',
  in_review:  'bg-sky-50 text-sky-700 border border-sky-200',
  quoted:     'bg-indigo-50 text-indigo-700 border border-indigo-200',
  completed:  'bg-emerald-50 text-emerald-700 border border-emerald-200',
  declined:   'bg-red-50 text-red-700 border border-red-200',
};

function formatCurrencyFromCents(cents) {
  if (cents === null || cents === undefined) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function shortDate(iso) {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

export default function AdminEngravingQuotes() {
  const [statusFilter, setStatusFilter] = useState('new');
  const [requests, setRequests] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/engraving-quotes?status=${statusFilter}`);
      setRequests(data.requests || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const claim = async (req) => {
    try {
      await api.post(`/admin/engraving-quotes/${req.request_id}/claim`);
      toast.success('Claimed — marked as In review.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const respond = async (req) => {
    const dollars = window.prompt(
      `Send quote to ${req.contact_name}. Enter quoted amount in USD (e.g. 850.00):`,
      req.quoted_amount_cents ? (req.quoted_amount_cents / 100).toFixed(2) : '',
    );
    if (dollars === null) return;
    const num = Number(dollars);
    if (!Number.isFinite(num) || num < 0) {
      toast.error('Enter a valid amount in dollars.');
      return;
    }
    const next_step = window.prompt(
      'Next step / instructions for the family (optional, plain text):',
      req.next_step || 'We will follow up by email within 2 business days.',
    );
    if (next_step === null) return;
    const internal = window.prompt(
      'Internal note to record on this quote (optional):',
      '',
    );
    if (internal === null) return;
    try {
      await api.post(`/admin/engraving-quotes/${req.request_id}/respond`, {
        quoted_amount_cents: Math.round(num * 100),
        next_step: next_step.trim() || null,
        admin_notes: internal.trim() || null,
      });
      toast.success(`Quote recorded: ${formatCurrencyFromCents(Math.round(num * 100))}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const previewPdf = (req) => {
    // Auth is cookie-based (`withCredentials: true`). We open a fetch
    // (NOT a direct anchor) so we can surface error JSON in the UI.
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    fetch(`${base}/api/admin/engraving-quotes/${req.request_id}/pdf`, {
      credentials: 'include',
    }).then(async (resp) => {
      if (!resp.ok) {
        const t = await resp.text();
        try {
          const j = JSON.parse(t);
          toast.error(j.detail || 'Could not download PDF');
        } catch {
          toast.error('Could not download PDF');
        }
        return;
      }
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    }).catch(() => toast.error('Could not download PDF'));
  };

  const sendEmail = async (req, { resend = false } = {}) => {
    const defaultTo = req.quote_email_last_recipient || req.contact_email || '';
    const verb = resend ? 'Resend' : 'Send';
    const to = window.prompt(
      `${verb} the engraving quote PDF to:`,
      defaultTo,
    );
    if (to === null) return;
    const recipient = (to || '').trim().toLowerCase();
    if (!recipient || !recipient.includes('@')) {
      toast.error('Enter a valid email address.');
      return;
    }
    if (!window.confirm(`Email the quote PDF (${formatCurrencyFromCents(req.quoted_amount_cents)}) to ${recipient}?`)) return;
    try {
      const { data } = await api.post(
        `/admin/engraving-quotes/${req.request_id}/send-email`,
        { recipient_email: recipient },
      );
      if (data?.delivered) {
        toast.success(`Quote emailed to ${recipient}`);
      } else if (data?.provider_reason === 'not_configured') {
        toast.warning('SendGrid is not configured — audit trail recorded. Please send the PDF manually for now.');
      } else {
        toast.warning(`Recorded as attempted (${data?.provider_reason || 'no provider response'}).`);
      }
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addNote = async (req) => {
    const txt = window.prompt(`Add an internal note to ${req.deceased_full_name}'s quote:`, '');
    if (txt === null) return;
    if (!txt.trim()) return;
    try {
      await api.post(`/admin/engraving-quotes/${req.request_id}/note`, { admin_notes: txt.trim() });
      toast.success('Note added.');
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const setStatus = async (req, status) => {
    let reason = null;
    if (status === 'declined') {
      reason = window.prompt('Reason for declining (visible internally):', '');
      if (reason === null) return;
    } else if (status === 'completed') {
      if (!window.confirm(`Mark ${req.deceased_full_name}'s engraving quote as completed?`)) return;
    }
    try {
      await api.post(`/admin/engraving-quotes/${req.request_id}/status`, {
        status, reason: reason || null,
      });
      toast.success(`Marked ${status.replace('_', ' ')}.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
          <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-engraving-quotes-title">
            Headstone engraving quotes
          </h1>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
            Manual workflow. Coordinate directly with the cemetery, funeral home, or engraver — record the quoted price here so the family always has a written record.
          </p>
        </header>

        <div className="flex flex-wrap gap-2 mb-8">
          {STATUS_TABS.map((t) => {
            const c = counts[t.id];
            const showBadge = (t.id !== 'all') && Number.isFinite(c);
            return (
              <button
                key={t.id}
                onClick={() => setStatusFilter(t.id)}
                className={`px-4 py-2 rounded-full text-sm border transition flex items-center gap-2 ${
                  statusFilter === t.id
                    ? 'bg-[hsl(var(--primary))] text-[hsl(var(--accent))] border-[hsl(var(--primary))]'
                    : 'border-[hsl(var(--primary)/0.18)] text-[hsl(var(--primary)/0.8)] hover:bg-[hsl(var(--primary)/0.04)]'
                }`}
                data-testid={`admin-engraving-tab-${t.id}`}
              >
                <span>{t.label}</span>
                {showBadge && (
                  <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                    statusFilter === t.id
                      ? 'bg-[hsl(var(--accent))]/20 text-[hsl(var(--accent))]'
                      : 'bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary)/0.7)]'
                  }`}>{c}</span>
                )}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-engraving-empty">
            No engraving quote requests match this filter.
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((req) => {
              const isOpen = expandedId === req.request_id;
              return (
                <div
                  key={req.request_id}
                  className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] px-6 py-5"
                  data-testid={`admin-engraving-row-${req.request_id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">
                        Engraving quote · {req.request_id.replace('engreq_', '')}
                      </div>
                      <h3 className="font-serif text-xl text-[hsl(var(--primary))] mt-1" data-testid={`admin-engraving-deceased-${req.request_id}`}>
                        {req.deceased_full_name}
                      </h3>
                      <div className="text-sm text-[hsl(var(--primary)/0.7)] mt-1">
                        Submitted by <strong>{req.contact_name}</strong> · {req.contact_email}
                        {req.contact_phone ? ` · ${req.contact_phone}` : ''}
                      </div>
                      <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">
                        {req.cemetery_name} · {req.cemetery_address}
                      </div>
                      <div className="text-xs text-[hsl(var(--primary)/0.55)] mt-1">
                        Submitted {shortDate(req.created_at)}
                        {req.quoted_at ? ` · Quoted ${shortDate(req.quoted_at)}` : ''}
                        {req.completed_at ? ` · Completed ${shortDate(req.completed_at)}` : ''}
                      </div>
                      {req.quoted_amount_cents !== null && req.quoted_amount_cents !== undefined && (
                        <div className="mt-2 inline-flex items-center gap-2 text-sm">
                          <span className="text-[hsl(var(--primary)/0.6)]">Quote:</span>
                          <span className="font-semibold text-[hsl(var(--primary))]" data-testid={`admin-engraving-amount-${req.request_id}`}>
                            {formatCurrencyFromCents(req.quoted_amount_cents)}
                          </span>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedId(isOpen ? null : req.request_id)}
                        className="mt-3 text-xs text-[hsl(var(--accent))] hover:underline"
                        data-testid={`admin-engraving-expand-${req.request_id}`}
                      >
                        {isOpen ? 'Hide details' : 'View full details'}
                      </button>

                      {isOpen && (
                        <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3 text-xs bg-[hsl(var(--primary)/0.04)] border border-[hsl(var(--primary)/0.1)] rounded p-4" data-testid={`admin-engraving-details-${req.request_id}`}>
                          <Detail label="Family name"     value={req.family_name} />
                          <Detail label="Date of birth"   value={req.date_of_birth} />
                          <Detail label="Date of passing" value={req.date_of_passing} />
                          <Detail label="Cemetery phone"  value={req.cemetery_phone} />
                          <Detail label="Stone material"  value={req.stone_material} />
                          <Detail label="Preferred placement" value={req.preferred_placement} />
                          {req.notes && (
                            <div className="sm:col-span-2">
                              <Detail label="Family notes" value={req.notes} multiline />
                            </div>
                          )}
                          {req.next_step && (
                            <div className="sm:col-span-2">
                              <Detail label="Next step (sent to family)" value={req.next_step} multiline />
                            </div>
                          )}
                          {req.admin_notes && (
                            <div className="sm:col-span-2">
                              <Detail label="Internal admin notes" value={req.admin_notes} multiline />
                            </div>
                          )}
                          {req.decline_reason && (
                            <div className="sm:col-span-2">
                              <Detail label="Decline reason" value={req.decline_reason} multiline />
                            </div>
                          )}
                          {Array.isArray(req.quote_email_history) && req.quote_email_history.length > 0 && (
                            <div className="sm:col-span-2 mt-1">
                              <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">Email history</div>
                              <ul className="mt-2 space-y-1.5">
                                {req.quote_email_history.map((h, idx) => (
                                  <li
                                    key={`${req.request_id}-em-${idx}`}
                                    className="text-[11px] text-[hsl(var(--primary)/0.85)] flex flex-wrap gap-x-2"
                                    data-testid={`admin-engraving-email-row-${req.request_id}-${idx}`}
                                  >
                                    <span>{h.delivered ? '✓' : '⚠'}</span>
                                    <span className="font-mono">{(h.sent_at || '').slice(0, 19).replace('T', ' ')}Z</span>
                                    <span>→ {h.recipient_email}</span>
                                    <span className="text-[hsl(var(--primary)/0.55)]">by {h.by_admin_name || '—'}</span>
                                    {!h.delivered && h.reason && (
                                      <span className="text-amber-700">({h.reason})</span>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col items-end gap-2">
                      <span
                        className={`text-xs uppercase tracking-wider px-3 py-1 rounded-full ${STATUS_PILL[req.status] || STATUS_PILL.new}`}
                        data-testid={`admin-engraving-status-${req.request_id}`}
                      >
                        {(req.status || 'new').replace('_', ' ')}
                      </span>

                      <div className="flex flex-wrap gap-2 justify-end max-w-[260px]">
                        {(!req.status || req.status === 'new') && (
                          <button
                            onClick={() => claim(req)}
                            className="ml-btn-secondary text-xs px-3 py-1.5"
                            data-testid={`admin-engraving-claim-${req.request_id}`}
                          >
                            Claim
                          </button>
                        )}
                        {req.status !== 'completed' && req.status !== 'declined' && (
                          <button
                            onClick={() => respond(req)}
                            className="ml-btn-gold text-xs px-3 py-1.5"
                            data-testid={`admin-engraving-respond-${req.request_id}`}
                          >
                            {req.status === 'quoted' ? 'Update quote' : 'Send quote'}
                          </button>
                        )}
                        {req.quoted_amount_cents !== null && req.quoted_amount_cents !== undefined && (
                          <>
                            <button
                              onClick={() => previewPdf(req)}
                              className="ml-btn-secondary text-xs px-3 py-1.5"
                              data-testid={`admin-engraving-pdf-${req.request_id}`}
                            >
                              Preview PDF
                            </button>
                            <button
                              onClick={() => sendEmail(req, { resend: (req.quote_email_send_count || 0) > 0 })}
                              className="ml-btn-primary text-xs px-3 py-1.5"
                              data-testid={`admin-engraving-email-${req.request_id}`}
                            >
                              {(req.quote_email_send_count || 0) > 0 ? 'Resend email' : 'Email to family'}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => addNote(req)}
                          className="ml-btn-secondary text-xs px-3 py-1.5"
                          data-testid={`admin-engraving-note-${req.request_id}`}
                        >
                          Add note
                        </button>
                        {req.status === 'quoted' && (
                          <button
                            onClick={() => setStatus(req, 'completed')}
                            className="ml-btn-primary text-xs px-3 py-1.5"
                            data-testid={`admin-engraving-complete-${req.request_id}`}
                          >
                            Mark completed
                          </button>
                        )}
                        {req.status !== 'completed' && req.status !== 'declined' && (
                          <button
                            onClick={() => setStatus(req, 'declined')}
                            className="ml-btn-secondary text-xs px-3 py-1.5 text-red-700"
                            data-testid={`admin-engraving-decline-${req.request_id}`}
                          >
                            Decline
                          </button>
                        )}
                      </div>
                      {req.quote_email_last_sent_at && (
                        <div
                          className="mt-2 text-[10px] uppercase tracking-[0.16em] text-[hsl(var(--primary)/0.6)] text-right"
                          data-testid={`admin-engraving-email-history-${req.request_id}`}
                        >
                          {req.quote_email_last_delivered ? '✓ Sent' : '⚠ Recorded'} ·{' '}
                          {shortDate(req.quote_email_last_sent_at)} ·{' '}
                          {req.quote_email_send_count || 1} send{(req.quote_email_send_count || 1) === 1 ? '' : 's'}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </AdminLayout>
    </div>
  );
}

function Detail({ label, value, multiline }) {
  return (
    <div>
      <div className="uppercase tracking-[0.16em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</div>
      <div className={`text-[hsl(var(--primary))] mt-1 ${multiline ? 'whitespace-pre-wrap' : ''}`}>
        {value || '—'}
      </div>
    </div>
  );
}
